import type {
  BatchPrintTaskRef,
  ConflictCode,
  TaskListResponse,
} from '../../services/api/types.gen';
import type { ConfirmSpec } from '../SelectionActionBar';
import { getScheduleLockedTask } from './lockedTask';
import {
  getTaskRowClassification,
  TASK_TYPE_FILTER_TABS,
  type TaskTypeFilterValue,
} from './taskTypeFilters';

/**
 * Pure helpers backing the Episode Manager batch-actions bar (ROG-2506).
 *
 * These hold the eligibility rules, error-code copy, and payload shaping so the
 * giant PatientDetail page stays thin and these decisions stay unit-testable.
 */

type TaskRef = Pick<TaskListResponse, 'task_type' | 'status'>;

/**
 * A task can be batch-deleted only when it is not schedule-locked (signed /
 * sent / completed) and is not a patient communication. Communications and
 * locked rows are surfaced as skipped so the confirm dialog can disclose them.
 */
export function partitionDeletable<T extends TaskRef>(
  tasks: T[]
): { deletable: T[]; skippedLocked: T[]; skippedComm: T[] } {
  const deletable: T[] = [];
  const skippedLocked: T[] = [];
  const skippedComm: T[] = [];
  for (const task of tasks) {
    if (task.task_type === 'patient_communication') {
      skippedComm.push(task);
    } else if (getScheduleLockedTask(task) !== null) {
      skippedLocked.push(task);
    } else {
      deletable.push(task);
    }
  }
  return { deletable, skippedLocked, skippedComm };
}

/**
 * Split a selection by whether work has begun. Not-started rows print as blank
 * template forms, which the print confirm gate discloses before exporting.
 */
export function partitionNotStarted<T extends Pick<TaskListResponse, 'status'>>(
  tasks: T[]
): { notStarted: T[]; started: T[] } {
  const notStarted: T[] = [];
  const started: T[] = [];
  for (const task of tasks) {
    if (task.status === 'not_started') {
      notStarted.push(task);
    } else {
      started.push(task);
    }
  }
  return { notStarted, started };
}

/**
 * Map a delete-failure {@link ConflictCode} to short, user-facing copy. The
 * server `detail` is intentionally never surfaced (see
 * `.cursor/rules/error-handling.mdc`); we key off the stable `code` instead.
 * Unknown / missing codes degrade to a generic phrase.
 */
export function deleteConflictCopy(
  code?: ConflictCode | string | null
): string {
  switch (code) {
    case 'VISIT_LINKED_TO_ORDER':
      return 'linked to a CMS-485 — delete the linked order first';
    case 'VISIT_LOCKED':
      return 'signed or completed';
    case 'ORDER_LOCKED':
      return 'signed or sent';
    case 'COMMUNICATION_LOCKED':
      return 'completed';
    case 'MISC_TASK_LOCKED':
      return 'completed';
    default:
      return "couldn't be deleted";
  }
}

/**
 * Task types that have a rendered PDF. Mirrors the backend
 * `BatchPrintTaskRef.task_type` literal, which rejects anything else with a 422,
 * so rows of any other type must be filtered out before the request is built.
 * Shared with the per-row print affordance (`hasPrintView` in `usePrintView`) so
 * the row button and the batch filter can never disagree.
 */
export const PRINTABLE_TASK_TYPES = new Set<BatchPrintTaskRef['task_type']>([
  'visit',
  'order',
  'patient_communication',
]);

/**
 * Split a selection by whether the task has a print view at all. Misc tasks have
 * no rendered document, so they are disclosed as skipped rather than sent to the
 * endpoint and rejected.
 */
export function partitionPrintable<
  T extends Pick<TaskListResponse, 'task_type'>,
>(tasks: T[]): { printable: T[]; skippedUnprintable: T[] } {
  const printable: T[] = [];
  const skippedUnprintable: T[] = [];
  for (const task of tasks) {
    if (
      PRINTABLE_TASK_TYPES.has(task.task_type as BatchPrintTaskRef['task_type'])
    ) {
      printable.push(task);
    } else {
      skippedUnprintable.push(task);
    }
  }
  return { printable, skippedUnprintable };
}

/** Shape the selected tasks into the batch-print endpoint's task refs. */
export function toBatchPrintTaskRefs(
  tasks: Pick<TaskListResponse, 'id' | 'task_type'>[]
): BatchPrintTaskRef[] {
  return partitionPrintable(tasks).printable.map(task => ({
    task_id: task.id,
    task_type: task.task_type as BatchPrintTaskRef['task_type'],
  }));
}

// The label shown for each manifest grouping. Therapy rows group by their
// specific discipline (PT/OT/ST) so the manifest reads as the clinician thinks,
// falling back to the generic "Therapy" tab label when the discipline is
// unknown.
const FILTER_LABEL_BY_VALUE: Record<TaskTypeFilterValue, string> =
  TASK_TYPE_FILTER_TABS.reduce(
    (acc, tab) => {
      acc[tab.value] = tab.label;
      return acc;
    },
    {} as Record<TaskTypeFilterValue, string>
  );

/**
 * The manifest grouping for a task: a stable `key` and the human label drawn
 * from {@link TASK_TYPE_FILTER_TABS}, so the bar's grouped manifest matches the
 * filter tabs the user already knows.
 */
export function taskManifestGroup(task: TaskListResponse): {
  key: string;
  label: string;
} {
  const classification = getTaskRowClassification(task);
  switch (classification.category) {
    case 'orders':
      return { key: 'orders', label: FILTER_LABEL_BY_VALUE.orders };
    case 'comm':
      return { key: 'comm', label: FILTER_LABEL_BY_VALUE.comm };
    case 'misc':
      return { key: 'misc', label: FILTER_LABEL_BY_VALUE.misc };
    case 'nursing':
      return { key: 'nursing', label: FILTER_LABEL_BY_VALUE.nursing };
    case 'msw':
      return { key: 'msw', label: FILTER_LABEL_BY_VALUE.msw };
    case 'hha':
      return { key: 'hha', label: FILTER_LABEL_BY_VALUE.hha };
    case 'therapy': {
      const discipline = classification.therapyDiscipline;
      if (discipline === 'PT') {
        return { key: 'pt', label: FILTER_LABEL_BY_VALUE.pt };
      }
      if (discipline === 'OT') {
        return { key: 'ot', label: FILTER_LABEL_BY_VALUE.ot };
      }
      if (discipline === 'ST') {
        return { key: 'st', label: FILTER_LABEL_BY_VALUE.st };
      }
      return { key: 'all_therapy', label: FILTER_LABEL_BY_VALUE.all_therapy };
    }
    default:
      return { key: 'other', label: 'Other' };
  }
}

/**
 * The confirm gate shared by both print actions.
 *
 * Returns null when there is nothing to disclose. Discloses not-started rows
 * (which print as blank templates) and rows with no printable document (which
 * are left out of the chart record). When nothing at all can print, it says so
 * and offers only a dismiss, because "Continue" would only reach the error path.
 */
export function buildPrintConfirm(
  items: TaskListResponse[]
): ConfirmSpec | null {
  const { notStarted } = partitionNotStarted(items);
  const { skippedUnprintable } = partitionPrintable(items);
  const blanks = notStarted.length;
  const unprintable = skippedUnprintable.length;
  if (blanks === 0 && unprintable === 0) return null;

  if (unprintable === items.length) {
    return {
      title: 'Nothing to print',
      body: 'The selected tasks have no printable document.',
      choices: [{ key: 'cancel', label: 'Close', variant: 'outline' }],
    };
  }

  const parts: string[] = [];
  if (blanks > 0) {
    parts.push(
      `${blanks} selected task${blanks === 1 ? '' : 's'} ${
        blanks === 1 ? "hasn't" : "haven't"
      } been started and will print as blank template forms.`
    );
  }
  if (unprintable > 0) {
    parts.push(
      `${unprintable} selected task${
        unprintable === 1 ? ' has' : 's have'
      } no printable document and will be left out of the chart record.`
    );
  }
  return {
    title:
      blanks > 0 ? "Some tasks haven't started" : 'Some tasks cannot print',
    body: parts.join(' '),
    choices: [
      { key: 'cancel', label: 'Cancel', variant: 'outline' },
      {
        key: 'completed',
        label: 'Print only completed',
        disabled: blanks === 0 || blanks === items.length,
      },
      { key: 'all', label: blanks > 0 ? 'Include blank forms' : 'Continue' },
    ],
  };
}
